import { Box, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import type { InvoiceHistoryAction, InvoiceHistoryEntry } from '@/components/invoices/invoice.types'
import { getInvoiceHistory } from '@/api/invoices.api'

const ACTION_LABELS: Record<InvoiceHistoryAction, string> = {
  CREATED: 'Erstellt',
  APPROVED: 'Freigegeben',
  REJECTED: 'Abgelehnt',
  CANCELED: 'Storniert',
  CANCELLATION_CREATED: 'Stornorechnung erstellt',
  PDF_REGENERATED: 'PDF neu generiert',
  BATCH_REGENERATED: 'Neu generiert (Stapel)',
}

// Chakra color token prefix per action, used for the timeline dot.
const ACTION_COLORS: Record<InvoiceHistoryAction, string> = {
  CREATED: 'blue',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELED: 'red',
  CANCELLATION_CREATED: 'orange',
  PDF_REGENERATED: 'gray',
  BATCH_REGENERATED: 'gray',
}

function formatDateTime(value: string) {
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export function InvoiceAuditLog({ invoiceId }: { invoiceId: string }) {
  const [entries, setEntries] = useState<InvoiceHistoryEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!invoiceId) return

    let mounted = true

    async function load() {
      try {
        setIsLoading(true)
        setError(null)
        const data = await getInvoiceHistory(invoiceId)
        if (mounted) setEntries(data)
      } catch (e) {
        if (mounted) {
          setError(e instanceof Error ? e.message : 'Der Verlauf konnte nicht geladen werden.')
        }
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    load()

    return () => {
      mounted = false
    }
  }, [invoiceId])

  return (
    <Box
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="16px"
      bg="bg.default"
      px={4}
      py={5}
    >
      <Text fontWeight="500" color="fg" mb={4}>
        Verlauf
      </Text>

      {isLoading ? (
        <Text fontSize="sm" color="fg.muted">
          Lädt...
        </Text>
      ) : error ? (
        <Text fontSize="sm" color="red.fg">
          {error}
        </Text>
      ) : entries.length === 0 ? (
        <Text fontSize="sm" color="fg.muted">
          Keine Verlaufseinträge vorhanden.
        </Text>
      ) : (
        <VStack align="stretch" gap={0}>
          {entries.map((entry, index) => {
            const color = ACTION_COLORS[entry.action] ?? 'gray'
            const isLast = index === entries.length - 1
            return (
              <HStack key={entry.id} align="stretch" gap={3}>
                {/* timeline rail */}
                <Flex direction="column" align="center" minW="14px">
                  <Box w="10px" h="10px" mt="6px" borderRadius="full" bg={`${color}.500`} />
                  {!isLast && <Box flex="1" w="2px" bg="border.default" my={1} />}
                </Flex>

                <Box pb={isLast ? 0 : 5} flex="1">
                  <Flex justify="space-between" align="baseline" gap={3} wrap="wrap">
                    <Text fontSize="sm" fontWeight="500" color="fg">
                      {ACTION_LABELS[entry.action] ?? entry.action}
                    </Text>
                    <Text fontSize="xs" color="fg.muted" whiteSpace="nowrap">
                      {formatDateTime(entry.timestamp)}
                    </Text>
                  </Flex>

                  {entry.username && (
                    <Text fontSize="xs" color="fg.muted" mt={0.5}>
                      {entry.username}
                    </Text>
                  )}

                  {entry.reason && (
                    <Text fontSize="sm" color="fg" mt={1}>
                      {entry.reason}
                    </Text>
                  )}
                </Box>
              </HStack>
            )
          })}
        </VStack>
      )}
    </Box>
  )
}
